import { useEffect, useRef } from 'react';

// Helpers for smooth color interpolation
const parseRgba = (rgba: string) => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) return [0, 0, 0, 1];
  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3]), match[4] ? parseFloat(match[4]) : 1];
};

const formatRgba = (r: number, g: number, b: number, a: number) => {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})`;
};

const lerp = (start: number, end: number, t: number) => {
  return start * (1 - t) + end * t;
};

/**
 * Custom hook to dynamically update the theme-color meta tag (controls device header/footer bars)
 * and website footer background. Syncs with active section and dark mode.
 */
export function useThemeColor(activeSection: string, isDarkMode: boolean) {
  const animationRef = useRef<number>();
  const currentColorRef = useRef<string | null>(null);

  useEffect(() => {
    // Get or create the theme-color meta tag (controls device UI bars)
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }

    // Also create/update apple-mobile-web-app-status-bar-style for iOS
    let appleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleStatusBar) {
      appleStatusBar = document.createElement('meta');
      appleStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
      document.head.appendChild(appleStatusBar);
    }

    // Use a smooth warm progression so browser chrome feels continuous with the page.
    const getThemeColor = () => {
      if (isDarkMode) {
        const darkColors: Record<string, string> = {
          home: 'rgba(26, 15, 10, 0.96)',
          bhalil: 'rgba(61, 35, 24, 0.96)',
          rooms: 'rgba(55, 32, 20, 0.96)',
          services: 'rgba(49, 28, 16, 0.96)',
          activities: 'rgba(43, 24, 12, 0.96)',
          gallery: 'rgba(37, 20, 8, 0.96)',
          nearby: 'rgba(31, 16, 4, 0.96)',
          guides: 'rgba(22, 10, 0, 0.96)',
          faq: 'rgba(13, 5, 0, 0.96)',
          reviews: 'rgba(19, 8, 0, 0.96)',
          findus: 'rgba(16, 6, 0, 0.96)',
          contact: 'rgba(10, 3, 0, 0.96)',
        };
        return darkColors[activeSection] || darkColors.home;
      } else {
        const lightColors: Record<string, string> = {
          home: 'rgba(250, 248, 245, 0.88)',
          bhalil: 'rgba(235, 224, 200, 0.88)',
          rooms: 'rgba(235, 224, 200, 0.88)',
          services: 'rgba(224, 208, 168, 0.88)',
          activities: 'rgba(210, 184, 136, 0.88)',
          gallery: 'rgba(196, 160, 104, 0.88)',
          nearby: 'rgba(165, 124, 72, 0.88)',
          guides: 'rgba(155, 112, 56, 0.90)',
          faq: 'rgba(139, 90, 43, 0.90)',
          reviews: 'rgba(150, 107, 51, 0.90)',
          findus: 'rgba(150, 107, 51, 0.90)',
          contact: 'rgba(139, 90, 43, 0.90)',
        };
        return lightColors[activeSection] || lightColors.home;
      }
    };

    const targetColor = getThemeColor();
    
    // Update iOS status bar style (use 'black-translucent' for dark, 'default' for light)
    appleStatusBar.setAttribute('content', isDarkMode ? 'black-translucent' : 'default');

    // Initial setup without animation or when switching dark mode
    if (!currentColorRef.current) {
      currentColorRef.current = targetColor;
      metaThemeColor.setAttribute('content', targetColor);
      return;
    }

    if (currentColorRef.current === targetColor) {
      return;
    }

    // Animate color transition
    const startColor = parseRgba(currentColorRef.current);
    const endColor = parseRgba(targetColor);
    const duration = 600; // 600ms transition
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function (ease-out cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const r = lerp(startColor[0], endColor[0], easeProgress);
      const g = lerp(startColor[1], endColor[1], easeProgress);
      const b = lerp(startColor[2], endColor[2], easeProgress);
      const a = lerp(startColor[3], endColor[3], easeProgress);

      const newColor = formatRgba(r, g, b, a);
      currentColorRef.current = newColor;
      metaThemeColor.setAttribute('content', newColor);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [activeSection, isDarkMode]); // Update when section or dark mode changes
}